import setDynamicFormFieldValue from "@bevco/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@bevco/Store/Reducers";
import { DynamicFormContext } from "@bevco/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@bevco/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@bevco/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField from "@insite/mobius/TextField";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    minValue = "minValue",
    maxValue = "maxValue",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.minValue]: number | null;
        [fields.maxValue]: number | null;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "quantity";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Validates that a string is an integer or decimal number. */
const isNumericString = (value: string) => /^-?\d+(\.\d+)?$/.test(value.trim());

/** Salesforce Dynamic Form numeric field widget. */
const DynamicNumeric: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, hintText, isRequired, minValue, maxValue } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    useRegisterDynamicFormFieldKeys(id, [fieldKey]);

    const validate = React.useCallback(() => {
        const value = fieldValue == null ? "" : String(fieldValue).trim();
        let error = "";

        if (!value) {
            if (isRequired) {
                error = siteMessage("Field_Required", label) as string;
            }
        } else if (!isNumericString(value)) {
            error = translate("{0} must be a number", label);
        } else {
            const numeric = Number(value);
            if (minValue != null && !Number.isNaN(minValue) && numeric < minValue) {
                error = translate("{0} must be at least {1}", label, String(minValue));
            } else if (maxValue != null && !Number.isNaN(maxValue) && numeric > maxValue) {
                error = translate("{0} cannot exceed {1}", label, String(maxValue));
            }
        }

        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired, label, minValue, maxValue]);

    React.useEffect(() => {
        formContext.validators[fieldKey] = validate;
        if (typeof fieldValue !== "undefined") {
            validate();
        }
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, fieldValue, validate, formContext.validators]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = event.target.value;
        // Allow empty, digits, one decimal, optional leading minus while typing.
        if (next === "" || /^-?\d*\.?\d*$/.test(next)) {
            setDynamicFormFieldValue({ key: fieldKey, value: next });
        }
    };

    return (
        <DynamicFormFieldShell>
            <TextField
                type="text"
                inputMode="decimal"
                label={label}
                required={isRequired}
                placeholder={hintText}
                value={fieldValue || ""}
                error={errorMessage}
                onChange={handleChange}
            />
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicNumeric),
    definition: {
        group: widgetGroup,
        displayName: "Numeric",
        icon: "hashtag",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.minValue,
                displayName: "Minimum Value",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: null,
                placeholder: "No minimum",
            },
            {
                name: fields.maxValue,
                displayName: "Maximum Value",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: null,
                placeholder: "No maximum",
            },
        ],
    },
};

export default widgetModule;
